import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';

const VolumeIcon = ({ size = 24, color = '#000000' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5L6 9H2V15H6L11 19V5Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15.54 8.46C16.4774 9.39764 17.0039 10.6692 17.0039 11.995C17.0039 13.3208 16.4774 14.5924 15.54 15.53" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M18.24 5.76C19.9779 7.49786 20.9388 9.84435 20.9388 12.285C20.9388 14.7257 19.9779 17.0721 18.24 18.81" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Ball = ({ x, y, radius, color }) => (
  <div style={{
    position: 'absolute',
    left: x - radius,
    top: y - radius,
    width: radius * 2,
    height: radius * 2,
    borderRadius: '50%',
    backgroundColor: color,
  }} />
);

const SwitchClicker = () => {
  const [isOn, setIsOn] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [balls, setBalls] = useState([]);
  const audioRef = useRef(new Audio('/click.mp3'));
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  const ballRadius = 10; 
  const ballColor = isOn ? '#333' : '#f0f0f0'; 
  const BALL_CREATION_PROBABILITY = 1;
  const playSound = () => {
    audioRef.current.volume = volume;
    audioRef.current.currentTime = 0.1;
    audioRef.current.play();
  };

  const handlePress = () => {
    setIsPressed(true);
  };

  const handleRelease = () => {
    setIsPressed(false);
    setIsOn(!isOn);
    setShowVolumeSlider(false);
    playSound();
    if (Math.random() < BALL_CREATION_PROBABILITY) {
      createBall();
    }

  };

  const handleVolumeChange = (e) => {
    setVolume(e.target.value);
  };

  const toggleVolumeSlider = () => {
    setShowVolumeSlider(!showVolumeSlider);
  };

  const createBall = () => {
    const buttonRect = containerRef.current.getBoundingClientRect();
    const x = buttonRect.left + Math.random() * buttonRect.width;
    const y = buttonRect.top;
    const vx = (Math.random() - 0.5) * 5;
    const vy = -10 - Math.random() * 5;

    setBalls(prevBalls => [...prevBalls, { x, y, vx, vy, radius: ballRadius }]);
  };

  useEffect(() => {
    const gravity = 0.5;
    const friction = 0.99;
    const restitution = 0.7;

    const updateBalls = () => {
      setBalls(prevBalls => {
        const updatedBalls = prevBalls.map(ball => {
          let { x, y, vx, vy, radius } = ball;
          
          vy += gravity;
          x += vx;
          y += vy;

          // Screen boundaries
          if (x - radius < 0) {
            x = radius;
            vx *= -restitution;
          } else if (x + radius > window.innerWidth) {
            x = window.innerWidth - radius;
            vx *= -restitution;
          }

          if (y - radius < 0) {
            y = radius;
            vy *= -restitution;
          } else if (y + radius > window.innerHeight) {
            y = window.innerHeight - radius;
            vy *= -restitution;
          }

          // Apply friction
          vx *= friction;
          vy *= friction;

          return { ...ball, x, y, vx, vy };
        });

        // Improved ball-to-ball collisions
        for (let i = 0; i < updatedBalls.length; i++) {
          for (let j = i + 1; j < updatedBalls.length; j++) {
            const ball1 = updatedBalls[i];
            const ball2 = updatedBalls[j];
            const dx = ball2.x - ball1.x;
            const dy = ball2.y - ball1.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < ball1.radius + ball2.radius) {
              const angle = Math.atan2(dy, dx);
              const sin = Math.sin(angle);
              const cos = Math.cos(angle);

              // Rotate ball positions
              const pos1 = {x: 0, y: 0};
              const pos2 = rotate(dx, dy, sin, cos, true);

              // Rotate ball velocities
              const vel1 = rotate(ball1.vx, ball1.vy, sin, cos, true);
              const vel2 = rotate(ball2.vx, ball2.vy, sin, cos, true);

              // Collision reaction
              const vxTotal = vel1.x - vel2.x;
              vel1.x = ((ball1.radius - ball2.radius) * vel1.x + 2 * ball2.radius * vel2.x) / (ball1.radius + ball2.radius);
              vel2.x = vxTotal + vel1.x;

              // Update position to prevent overlap
              const absV = Math.abs(vel1.x) + Math.abs(vel2.x);
              const overlap = (ball1.radius + ball2.radius) - Math.abs(pos1.x - pos2.x);
              pos1.x += vel1.x / absV * overlap;
              pos2.x += vel2.x / absV * overlap;

              // Rotate positions back
              const pos1F = rotate(pos1.x, pos1.y, sin, cos, false);
              const pos2F = rotate(pos2.x, pos2.y, sin, cos, false);

              // Adjust positions
              ball2.x = ball1.x + pos2F.x;
              ball2.y = ball1.y + pos2F.y;
              ball1.x = ball1.x + pos1F.x;
              ball1.y = ball1.y + pos1F.y;

              // Rotate velocities back
              const vel1F = rotate(vel1.x, vel1.y, sin, cos, false);
              const vel2F = rotate(vel2.x, vel2.y, sin, cos, false);

              ball1.vx = vel1F.x;
              ball1.vy = vel1F.y;
              ball2.vx = vel2F.x;
              ball2.vy = vel2F.y;
            }
          }
        }

        return updatedBalls;
      });

      animationRef.current = requestAnimationFrame(updateBalls);
    };

    updateBalls();

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Helper function for ball collision calculations
  const rotate = (x, y, sin, cos, reverse) => {
    return {
      x: (reverse) ? (x * cos + y * sin) : (x * cos - y * sin),
      y: (reverse) ? (y * cos - x * sin) : (y * cos + x * sin)
    };
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === 'Space' && !isPressed) {
        event.preventDefault();
        handlePress();
      }
    };

    const handleKeyUp = (event) => {
      if (event.code === 'Space') {
        event.preventDefault();
        handleRelease();
      }
    };

    const handleMouseLeave = () => {
      if (isPressed) {
        setIsPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isPressed]);

  return (
    <>
      <Helmet>
        <title>Seminerds</title>
        <meta name="description" content="Seminerds is an app development studio that specializes in viral and socially driven mobile apps." />
        <meta name="keywords" content="App development, mobile apps, viral apps, socially driven apps" />
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content="Seminerds" />
        <meta property="og:description" content="Seminerds is an app development studio that specializes in viral and socially driven mobile apps." />
        <meta property="og:type" content="website" />
        {/* You can add more meta tags as needed */}
      </Helmet>
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundColor: isOn ? '#f0f0f0' : '#333',
        transition: 'background-color 0.1s ease',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <div style={{
          position: 'absolute',
          top: '40px',
          right: '40px',
          color: isOn ? '#333' : '#f0f0f0',
          transition: 'color 0.1s ease',
          fontSize: '16px',
          fontWeight: '500',
        }}>
          heynerds@seminerds.com
        </div>

        <div style={{
          position: 'absolute',
          top: '40px',
          left: '40px',
          color: isOn ? '#333' : '#f0f0f0',
          transition: 'color 0.1s ease',
        }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 'bold',
            margin: 0,
            marginBottom: '8px',
          }}>
            Seminerds
          </h1>
          <p style={{
            fontSize: '24px',
            fontWeight: '500',
            margin: 0,
            maxWidth: '400px',
            lineHeight: '1.3',
          }}>
            We build mobile apps.
            <br />
            Viral.
            <br />
            Socially driven.
            <br />
            Super cool.
          </p>
        </div>

        <div 
          ref={containerRef}
          onMouseDown={handlePress}
          onMouseUp={handleRelease}
          onMouseLeave={() => setIsPressed(false)}
          style={{
            width: '200px',
            height: '40px',
            backgroundColor: '#ddd',
            borderRadius: '5px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.1s ease',
            transform: isPressed ? 'translateY(4px)' : 'translateY(0)',
            boxShadow: isPressed 
              ? 'inset 0 2px 5px rgba(0, 0, 0, 0.2)' 
              : '0 4px 0 #bbb, 0 5px 5px rgba(0, 0, 0, 0.2)'
          }}
        >
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
            color: '#555',
            userSelect: 'none'
          }}>
            SPACE
          </div>
        </div>
        {balls.map((ball, index) => (
          <Ball key={index} {...ball} color={ballColor} />
        ))}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          display: 'flex',
          flexDirection: 'column-reverse',
          alignItems: 'center'
        }}>
          <button 
            onClick={toggleVolumeSlider}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '5px',
              marginTop: showVolumeSlider ? '10px' : '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <VolumeIcon size={24} color={isOn ? '#333' : '#f0f0f0'} />
          </button>
          {showVolumeSlider && (
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.1" 
              value={volume} 
              onChange={handleVolumeChange}
              style={{
                writingMode: 'bt-lr',
                WebkitAppearance: 'slider-vertical',
                width: '8px',
                height: '100px',
                padding: '0 5px'
              }}
            />
          )}
        </div>
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          color: isOn ? '#333' : '#f0f0f0',
          transition: 'color 0.1s ease',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <span>©</span>
          <span>2025 Seminerds LLC</span>
        </div>
      </div>
    </>
  );
};

export default SwitchClicker;